import logger from '~/services/logger';
import { useEffect } from 'react';
import type { LoaderFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import { ExternalScripts } from 'remix-utils/external-scripts';
import { ClientOnly } from 'remix-utils/client-only';
import type { ClientLoaderFunctionArgs } from '@remix-run/react';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import nProgressStyles from 'nprogress/nprogress.css?url';
import { getFaviconSrc } from './services/layout/index.server.ts';
import styles from './styles/app.css?url';
import {
  getGoogleAnalyticsUserId,
  getBrazeAnalyticsUserId,
  GoogleAnalytics,
} from './utils/analytics.tsx';
import { getConfig, requireMockServer } from './services/config/index.ts';
import { getAnalyticsSharedVars } from './services/analytics/shared-vars.server.ts';
import useProgressBar from './hooks/use-progress-bar.ts';
import initClientSentry from './services/logging/index.client.ts';
import { useAudience } from './hooks/use-audience.ts';
import { featureFlags } from './services/feature-flags/index.server.ts';
import { getPluginConfig } from './utils/get-plugin-configuration.ts';
import ga4Manifest from '~/services/plugins/google_analytics_4/config/manifest.ts';
import sentryManifest from '~/services/plugins/sentry/config/manifest.ts';
import audioPlayerManifest from '~/services/plugins/web-audio-player/config/manifest.ts';
import queueWebManifest from '~/services/plugins/queue-web/config/manifest.ts';
import playlistsManifest from '~/services/plugins/playlists-plugin/config/manifest.ts';
import popupMenuManifest from '~/services/plugins/popup-menu/config/manifest.ts';
import favoritesManifest from '~/services/plugins/local_storage_favourites_action/config/manifest';
import {
  getDefaultThemePluginConfig,
  isThemeV2Enabled,
} from './services/css-vars/get-theme-vars.ts';
import brazeAnalyticsManifest from '~/services/plugins/zapp-analytics-plugin-braze/config/manifest.ts';
import { useBrazeAnalytics } from './hooks/use-braze-analytics.ts';
import { setNewRelicUserId } from './utils/newrelic.server.ts';
import {
  getAudioPlayerScreen,
  getIsPlayerRoute,
  getIsQuickBrickPlayerBrightcove,
} from './services/router/index.server.ts';
import type { ExternalScriptsHandle } from 'remix-utils/external-scripts';
import cookieProManifest from '~/services/plugins/applicaster-cmp-cookie-pro/config/manifest';
import quickBrickLoginFlowManifest from '~/services/plugins/quick-brick-login-flow/config/manifest';
import oauthLoginManifest from '~/services/plugins/zapp_login_plugin_oauth_2_0/config/manifest.ts';
import contextSetterManifest from '~/services/plugins/zapp-context-setter/config/manifest.ts';
import { getPageCacheControlHeader } from './services/response/index.ts';
import { useHasNavigatedSinceMount } from './hooks/use-has-navigated-since-mount.ts';
import { AudioPlayerRoot } from './services/plugins/web-audio-player/components/index.client.tsx';
import { AlertModalHost } from './components/alert-modal/index.ts';
import { PopupMenuHost } from './services/plugins/popup-menu/components/host/index.ts';
import { ConfirmationToastHost } from './components/confirmation-toast/index.ts';
import { getGeneralContentHookBlockRedirectPath } from './services/hooks/general-content-screen-hook.ts';
import { useContextSetterRemoteFeed } from './hooks/use-context-setter-remote-feed.ts';
import { getContextSetterRemoteUrl } from './services/plugins/zapp-context-setter/utils/index.ts';
import { type Screen } from '~/services/layout/index.ts';

declare global {
  interface Window {
    versionInfo: any;
    isMock: any;
    environment: 'development' | 'production' | 'test';
    debugError: any;
    braze?: any;
    Sentry?: any;
    safari?: {
      pushNotification?: unknown;
    };
    isLoggedInRevalidationIntervalSeconds?: number;
  }
}

interface Handle<Data = unknown> extends ExternalScriptsHandle<Data> {
  id: string;
}

export const handle: Handle = {
  id: 'root',
  scripts({ data }: any) {
    if (!data?.shouldAddBrightcoveSsaiScript) return [];

    return [
      {
        id: 'brightcove-ssai-script',
        src: 'https://players.brightcove.net/sdk/v1.0.3/web-sdk/integrations/ssai/index.iife.min.js',
      },
    ];
  },
};

export function links() {
  return [
    {
      rel: 'preconnect',
      href: 'https://assets-secure.applicaster.com',
      crossOrigin: 'anonymous',
    },
    { rel: 'stylesheet', href: styles },
    { rel: 'stylesheet', href: '/fonts/' },
    { rel: 'stylesheet', href: nProgressStyles },
  ];
}

export async function loader(loaderFunctionArgs: LoaderFunctionArgs) {
  const { request } = loaderFunctionArgs;
  const blockPath = getGeneralContentHookBlockRedirectPath(request);
  if (blockPath) {
    throw new Response(null, {
      status: 302,
      headers: { Location: blockPath },
    });
  }

  const isMock: boolean = process.env?.MOCK ? true : false;
  const DISABLE_THIRD_PARTY_SCRIPTS: string | undefined =
    process.env?.DISABLE_THIRD_PARTY_SCRIPTS;

  if (isMock) requireMockServer();

  const {
    pluginConfigurations,
    layout: { id: layoutId, screens },
  } = await getConfig();

  setNewRelicUserId({
    pluginConfigurations,
    request,
  });

  const { customConfiguration: ga4 } = getPluginConfig<typeof ga4Manifest>(
    ga4Manifest,
    pluginConfigurations
  );

  const { measurement_id: gaTrackingId, gtm_tracking_id: gtmTrackingId } = ga4;

  const analyticsSharedVars = await getAnalyticsSharedVars(layoutId);

  const versionInfo = {
    APP_VERSION_NAME: process.env?.APP_VERSION_NAME,
    APP_VERSION_UUID: process.env?.APP_VERSION_UUID,
    APP_NAME: process.env?.app_name,
    APP_FAMILY: process.env?.app_family_id,
    SDK_VERSION: process.env?.sdk_version,
    ACCOUNT_ID: process.env?.accounts_account_id,
    AUDIENCE: process.env?.AUDIENCE,
  };

  const isThemeV2 = isThemeV2Enabled(pluginConfigurations);

  const defaultThemePluginConfig: any =
    getDefaultThemePluginConfig(pluginConfigurations);

  const cacheControl = getPageCacheControlHeader(pluginConfigurations);

  const { customConfiguration: brazeAnalytics } = getPluginConfig<
    typeof brazeAnalyticsManifest
  >(brazeAnalyticsManifest, pluginConfigurations);

  const { customConfiguration: sentry } = getPluginConfig<
    typeof sentryManifest
  >(sentryManifest, pluginConfigurations);

  const sentryClientSampleRate =
    Number(process.env.SENTRY_CLIENT_SAMPLE_RATE) ||
    sentry?.client_sample_rate ||
    1.0;

  const sentryClientTracesSampleRate =
    Number(process.env.SENTRY_CLIENT_TRACES_SAMPLE_RATE) ||
    sentry?.client_traces_sample_rate ||
    1.0;

  const isSentryClientDebugEnabled: boolean =
    process.env.SENTRY_CLIENT_DEBUG === 'true';

  const faviconSrc: string = getFaviconSrc();

  const shouldAddBrightcoveSsaiScript: boolean =
    getIsPlayerRoute(request) &&
    getIsQuickBrickPlayerBrightcove(pluginConfigurations);

  const { customConfiguration: cookiePro } = getPluginConfig<
    typeof cookieProManifest
  >(cookieProManifest, pluginConfigurations);

  const { cookie_pro_id: cookieProId } = cookiePro;

  const { customConfiguration: quickBrickLoginFlow } = getPluginConfig<
    typeof quickBrickLoginFlowManifest
  >(quickBrickLoginFlowManifest, pluginConfigurations, true);

  const { customConfiguration: oauthLogin } = getPluginConfig<
    typeof oauthLoginManifest
  >(oauthLoginManifest, pluginConfigurations, true);

  const isLoggedInRevalidationIntervalSeconds =
    quickBrickLoginFlow?.is_logged_in_revalidation_interval_seconds ??
    oauthLogin?.is_logged_in_revalidation_interval_seconds;

  const { customConfiguration: contextSetter } = getPluginConfig<
    typeof contextSetterManifest
  >(contextSetterManifest, pluginConfigurations);

  const contextSetterRemoteUrl: string =
    getContextSetterRemoteUrl(contextSetter);

  const audioPlayerScreen: Screen | undefined = getAudioPlayerScreen(screens);

  const audioPlayerConfig = {
    ...getPluginConfig<typeof audioPlayerManifest>(
      audioPlayerManifest,
      audioPlayerScreen
    ),
    id: audioPlayerScreen?.id,
  };

  const queueWebConfig = getPluginConfig<typeof queueWebManifest>(
    queueWebManifest,
    pluginConfigurations
  );

  const playlistConfig = getPluginConfig<typeof playlistsManifest>(
    playlistsManifest,
    pluginConfigurations
  );

  const popupMenuConfig = getPluginConfig<typeof popupMenuManifest>(
    popupMenuManifest,
    pluginConfigurations
  );

  const favoritesAction = getPluginConfig<typeof favoritesManifest>(
    favoritesManifest,
    pluginConfigurations
  );

  return json(
    {
      featureFlags,
      faviconSrc,
      isMock,
      versionInfo,
      gaTrackingId,
      analyticsSharedVars,
      sentryDSN: process.env?.SENTRY_DSN,
      gtmTrackingId: process.env?.GTM_TRACKING_ID || gtmTrackingId,
      gaUserId: '',
      sentryEnv: process.env?.SENTRY_ENV || process.env.NODE_ENV,
      sentrySampleRate: sentryClientSampleRate,
      sentryTracesSampleRate: sentryClientTracesSampleRate,
      sentryDebug: isSentryClientDebugEnabled,
      environment: process.env.NODE_ENV,
      isThemeV2,
      appBackgroundColor: defaultThemePluginConfig?.app_background_color,
      confirmationToastCloseIcon:
        defaultThemePluginConfig?.confirmation_toast_close_icon,
      alertModalCloseIcon:
        defaultThemePluginConfig?.alert_modal_close_button_asset,
      brazeAnalytics,
      brazeUserId: '',
      DISABLE_THIRD_PARTY_SCRIPTS,
      shouldAddBrightcoveSsaiScript,
      cookieProId,
      isLoggedInRevalidationIntervalSeconds,
      contextSetterRemoteUrl,
      audioPlayerConfig,
      audioPlayerScreen,
      queueWebConfig,
      playlistConfig,
      popupMenuConfig,
      favoritesAction,
    },
    {
      headers: {
        'Cache-Control': cacheControl,
      },
    }
  );
}

export const clientLoader = async ({
  serverLoader,
}: ClientLoaderFunctionArgs) => {
  const gaUserId = getGoogleAnalyticsUserId();
  const brazeUserId = getBrazeAnalyticsUserId();
  const serverLoaderData = (await serverLoader()) || {};

  return {
    ...serverLoaderData,
    gaUserId,
    brazeUserId,
  };
};

clientLoader.hydrate = true;

function App() {
  const {
    isMock,
    faviconSrc,
    gaTrackingId,
    gtmTrackingId,
    analyticsSharedVars,
    gaUserId,
    versionInfo,
    sentryDSN,
    sentryEnv,
    environment,
    appBackgroundColor,
    sentrySampleRate,
    sentryTracesSampleRate,
    sentryDebug,
    DISABLE_THIRD_PARTY_SCRIPTS,
    cookieProId,
    isLoggedInRevalidationIntervalSeconds,
    contextSetterRemoteUrl,
    audioPlayerScreen,
  } = useLoaderData<typeof loader>();

  if (typeof window !== 'undefined') {
    window.versionInfo = isMock
      ? { ...versionInfo, APP_VERSION_UUID: 'mock' }
      : versionInfo;
  }

  const hasNavigatedSinceMount = useHasNavigatedSinceMount();

  useProgressBar();

  useAudience(versionInfo);

  useContextSetterRemoteFeed(contextSetterRemoteUrl);

  useEffect(() => {
    // Hydration waits on the mock service worker. e2e polls this before interacting.
    document.documentElement.dataset.hydrated = 'true';

    window.environment = environment;
    window.isLoggedInRevalidationIntervalSeconds =
      isLoggedInRevalidationIntervalSeconds;

    initClientSentry({
      appVersionName: versionInfo.APP_VERSION_NAME,
      environment: sentryEnv,
      appVersion: versionInfo.APP_VERSION_UUID,
      dsn: sentryDSN,
      sampleRate: sentrySampleRate,
      tracesSampleRate: sentryTracesSampleRate,
      debug: sentryDebug,
      isMock,
    });
  }, []);

  useBrazeAnalytics();

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <link rel="icon" href={faviconSrc}></link>
        <Links />
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: `
               window.addEventListener('load', () => {
                const [nav] = performance.getEntriesByType("navigation");
                window.tti = nav ? Math.round(nav.domInteractive - nav.requestStart) : undefined;
              });
              `,
          }}
        />

        {!DISABLE_THIRD_PARTY_SCRIPTS && !!cookieProId && (
          <>
            <script
              src="https://cookie-cdn.cookiepro.com/scripttemplates/otSDKStub.js"
              type="text/javascript"
              data-domain-script={cookieProId}
              defer
            ></script>

            <script
              defer
              dangerouslySetInnerHTML={{
                __html: `function OptanonWrapper() {}`,
              }}
            />
          </>
        )}
      </head>
      <body
        style={{
          backgroundColor: appBackgroundColor,
        }}
        className="theme overflow-x-hidden overscroll-none antialiased scrollbar-hide"
      >
        <div className="flex min-h-screen flex-col">
          {!DISABLE_THIRD_PARTY_SCRIPTS && (
            <GoogleAnalytics
              gtmTrackingId={gtmTrackingId}
              gaTrackingId={gaTrackingId}
              userId={gaUserId}
              analyticsSharedVars={analyticsSharedVars}
            />
          )}
          <Outlet />
          <ClientOnly fallback={<></>}>
            {() => (audioPlayerScreen ? <AudioPlayerRoot /> : null)}
          </ClientOnly>
          <ClientOnly fallback={null}>
            {() => <ConfirmationToastHost />}
          </ClientOnly>
          <ClientOnly fallback={null}>{() => <AlertModalHost />}</ClientOnly>
          <ClientOnly fallback={null}>{() => <PopupMenuHost />}</ClientOnly>
          <script
            defer
            dangerouslySetInnerHTML={{
              __html: `window.isMock = ${JSON.stringify(isMock)}`,
            }}
          />
          <ExternalScripts />
          {hasNavigatedSinceMount && <ScrollRestoration />}
          <Scripts />
        </div>
      </body>
    </html>
  );
}

export default Sentry.withSentry(App);

const ErrorComponent = ({ errorStatusCode }: { errorStatusCode: number }) => {
  return (
    <html>
      <head>
        <title>Error</title>
        <Meta />
        <Links />
      </head>
      <body>
        <section className="flex h-screen items-center justify-center bg-gray-900">
          <div className="mx-auto mb-12 max-w-screen-xl px-4 py-8 lg:px-6 lg:py-16">
            <div className="mx-auto max-w-screen-sm text-center">
              <h1 className="mb-4 text-7xl font-extrabold tracking-tight text-gray-400 lg:text-9xl">
                {errorStatusCode}
              </h1>
              <p className="mb-4 text-3xl font-bold tracking-tight text-gray-400 md:text-4xl">
                {errorStatusCode === 404
                  ? 'Page Not Found'
                  : 'Internal Server Error'}
              </p>
              <a
                href="/"
                className="bg-primary-600 hover:bg-primary-800 focus:ring-primary-300 focus:ring-primary-900 my-4 inline-flex rounded-lg px-5 py-2.5 text-center text-sm font-medium text-white focus:outline-none focus:ring-4"
              >
                Back to Homepage
              </a>
            </div>
          </div>
        </section>
      </body>
    </html>
  );
};

export function ErrorBoundary() {
  const error: any = useRouteError();

  Sentry.captureRemixErrorBoundaryError(error);
  if (isRouteErrorResponse(error)) {
    return <ErrorComponent errorStatusCode={error?.status || 500} />;
  }

  logger.info(`###### ${error}`);

  if (typeof window !== 'undefined') {
    const message = error?.message || '';
    const stack = error?.stack || '';

    window.debugError = {
      message,
      stack,
    };
  }

  // #region Temporary Firefox server 500 workaround on interrupted fetch requests
  // To be removed once the issue is fixed
  const isFirefox = !!(
    typeof window !== 'undefined' &&
    window?.navigator?.userAgent?.toLowerCase()?.includes('firefox')
  );

  const isNetworkError =
    error?.toString() ===
    'TypeError: NetworkError when attempting to fetch resource.';

  if (isFirefox && isNetworkError) return <></>;
  // #endregion

  return <ErrorComponent errorStatusCode={error?.status || 500} />;
}
